import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo
} from 'react';
import { Product } from '../models/Product';
import { Selection } from '../models/Selection';
import { loadSelections, saveSelections } from '../utils/localStorage';

const SelectionsContext = createContext();

export function SelectionsProvider({ children }) {
    const [selections, setSelections] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);

    // Load from localStorage on mount
    useEffect(() => {
        const saved = loadSelections();
        if (saved && Array.isArray(saved)) {
            setSelections(saved.map(item => new Selection(item)));
        }
    }, []);

    // Actions
    const addSelection = useCallback((productData) => {
        setSelections(prev => {
            const exists = prev.find(s => s.id === productData.id);
            if (exists) return prev;

            const product = new Product(productData);
            const selection = new Selection({
                product,
                addedAt: new Date().toISOString(),
            });

            const updated = [...prev, selection];
            saveSelections(updated.map(s => s.toJSON()));
            return updated;
        });

        setIsOpen(true);
        setShowReviewForm(false);
    }, []);

    const removeSelection = useCallback((id) => {
        setSelections(prev => {
            const updated = prev.filter(s => s.id !== id);
            saveSelections(updated.map(s => s.toJSON()));
            return updated;
        });
    }, []);

    const clearSelections = useCallback(() => {
        setSelections([]);
        saveSelections([]);
    }, []);

    const openCart = useCallback(() => {
        setIsOpen(true);
        setShowReviewForm(false);
    }, []);

    const closeCart = useCallback(() => {
        setIsOpen(false);
        setShowReviewForm(false);
    }, []);

    const openReviewForm = useCallback(() => {
        setShowReviewForm(true);
    }, []);

    const closeReviewForm = useCallback(() => {
        setShowReviewForm(false);
    }, []);

    const value = useMemo(() => ({
        selections,
        isOpen,
        showReviewForm,
        selectionsCount: selections.length,
        hasSelections: selections.length > 0,
        addSelection,
        removeSelection,
        clearSelections,
        openCart,
        closeCart,
        openReviewForm,
        closeReviewForm,
    }), [
        selections,
        isOpen,
        showReviewForm,
        addSelection,
        removeSelection,
        clearSelections,
        openCart,
        closeCart,
        openReviewForm,
        closeReviewForm,
    ]);

    return (
        <SelectionsContext.Provider value={value}>
            {children}
        </SelectionsContext.Provider>
    );
}

export function useSelections() {
    const context = useContext(SelectionsContext);
    if (!context) {
        throw new Error('useSelections must be used within SelectionsProvider');
    }
    return context;
}
